/**
 * Sección **Competencia** de *Control de Precios*.
 *
 * Las dos piezas que el panel anterior mostraba en tarjetas separadas: las
 * **alertas** de cambio de precio (con su verificación) y las **estaciones en
 * seguimiento**.
 *
 * Aquí el seguimiento se agrupa **por estación** (antes era una fila por cada
 * producto, así que una estación con ocho productos ocupaba ocho entradas) y se
 * puede quitar la estación entera o sólo uno de sus productos.
 */

import { ArrowDownRight, ArrowUpRight, BellRing, Building2, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import type { AlertaPricing, CompetidorPricing } from '../pricing.types';
import { coincideBusqueda, fechaHora } from '../pricing-utils';

interface PreciosSeguimientoProps {
  competidores: CompetidorPricing[];
  alertas: AlertaPricing[];
  noLeidas: number;
  cargando: boolean;
  /** Hay una operación de seguimiento en curso. */
  ocupado: boolean;
  /** Hay precios cargados para poder verificar. */
  puedeVerificar: boolean;
  onVerificar: () => void;
  onMarcarTodas: () => void;
  onMarcarAlerta: (id: number) => void;
  onQuitarEstacion: (estacion: string, productos: number) => void;
  onQuitarProducto: (competidor: CompetidorPricing) => void;
}

export function PreciosSeguimiento({
  competidores,
  alertas,
  noLeidas,
  cargando,
  ocupado,
  puedeVerificar,
  onVerificar,
  onMarcarTodas,
  onMarcarAlerta,
  onQuitarEstacion,
  onQuitarProducto,
}: PreciosSeguimientoProps) {
  const [soloSinLeer, setSoloSinLeer] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  /** Cuántas estaciones hay en seguimiento, sin aplicar la búsqueda. */
  const totalEstaciones = useMemo(
    () => new Set(competidores.map((competidor) => competidor.estacion)).size,
    [competidores],
  );

  /** Estaciones en seguimiento con sus productos. */
  const estaciones = useMemo(() => {
    const grupos = new Map<string, CompetidorPricing[]>();

    for (const competidor of competidores) {
      const lista = grupos.get(competidor.estacion) ?? [];
      lista.push(competidor);
      grupos.set(competidor.estacion, lista);
    }

    return [...grupos.entries()]
      .filter(([estacion, productos]) =>
        coincideBusqueda(
          busqueda,
          estacion,
          ...productos.map((producto) => producto.producto),
        ),
      )
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [busqueda, competidores]);

  const alertasVisibles = useMemo(
    () =>
      alertas.filter((alerta) => {
        if (soloSinLeer && alerta.leido) return false;

        return coincideBusqueda(busqueda, alerta.estacion, alerta.producto);
      }),
    [alertas, busqueda, soloSinLeer],
  );

  if (cargando) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardContent className="space-y-3">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <BellRing className="size-4 text-brand-500" aria-hidden="true" />
              Alertas de competencia
              {noLeidas > 0 ? <Badge variant="warning">{formatNumber(noLeidas)} sin leer</Badge> : null}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={soloSinLeer ? 'primary' : 'secondary'}
                onClick={() => setSoloSinLeer((previo) => !previo)}
              >
                Sólo sin leer
              </Button>
              <Button size="sm" variant="secondary" onClick={onVerificar} disabled={ocupado || !puedeVerificar}>
                <Search className="mr-1.5 size-3.5" aria-hidden="true" />
                Verificar ahora
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onMarcarTodas}
                disabled={ocupado || noLeidas === 0}
              >
                Marcar todas leídas
              </Button>
            </div>
          </header>

          {alertasVisibles.length === 0 ? (
            <p className="text-sm text-ink-500">
              {alertas.length === 0
                ? 'Sin alertas todavía. Marca estaciones como competencia y verifica para vigilar sus precios.'
                : 'Ninguna alerta coincide con el filtro.'}
            </p>
          ) : (
            <ul className="max-h-[38vh] divide-y divide-ink-50 overflow-auto">
              {alertasVisibles.map((alerta) => (
                <li key={alerta.id} className="flex items-start justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm text-ink-900">
                      {alerta.tipo === 'subio' ? (
                        <ArrowUpRight className="size-4 shrink-0 text-rose-600" aria-hidden="true" />
                      ) : (
                        <ArrowDownRight
                          className="size-4 shrink-0 text-emerald-600"
                          aria-hidden="true"
                        />
                      )}
                      <span className="truncate font-medium">{alerta.estacion}</span>
                      {!alerta.leido ? <Badge variant="warning">nueva</Badge> : null}
                    </p>
                    <p className="text-xs text-ink-500">
                      {alerta.producto} · de {formatCurrency(alerta.precio_anterior ?? 0)} a{' '}
                      {formatCurrency(alerta.precio_nuevo ?? 0)} (
                      {(alerta.diferencia ?? 0) > 0 ? '+' : ''}
                      {formatCurrency(alerta.diferencia ?? 0)}) · {fechaHora(alerta.created_at)}
                    </p>
                  </div>
                  {!alerta.leido ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onMarcarAlerta(alerta.id)}
                      disabled={ocupado}
                    >
                      Marcar leída
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Building2 className="size-4 text-brand-500" aria-hidden="true" />
              Estaciones en seguimiento
              <Badge variant="outline">
                {formatNumber(estaciones.length)}
                {estaciones.length === totalEstaciones ? '' : ` de ${formatNumber(totalEstaciones)}`}{' '}
                estaciones ·{' '}
                {formatNumber(estaciones.reduce((total, [, productos]) => total + productos.length, 0))}{' '}
                productos
              </Badge>
            </h2>
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-ink-300"
                aria-hidden="true"
              />
              <input
                className="h-9 w-56 rounded-control border border-ink-200 bg-white pr-3 pl-8 text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                placeholder="Buscar estación o producto…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
                aria-label="Buscar en el seguimiento"
              />
            </div>
          </header>

          {estaciones.length === 0 ? (
            <p className="text-sm text-ink-500">
              {competidores.length === 0
                ? 'Todavía no hay ninguna estación en seguimiento: usa «Estación» en Cambios o en Precios para marcarla completa.'
                : 'Ninguna estación coincide con la búsqueda.'}
            </p>
          ) : (
            <ul className="max-h-[40vh] space-y-2 overflow-auto">
              {estaciones.map(([estacion, productos]) => (
                <li
                  key={estacion}
                  className="rounded-control border border-ink-100 bg-white p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">{estacion}</p>
                      <p className="text-xs text-ink-400">
                        {formatNumber(productos.length)} productos · último control{' '}
                        {fechaHora(productos[0]?.ultima_verificacion)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onQuitarEstacion(estacion, productos.length)}
                      disabled={ocupado}
                      title="Quitar la estación completa del seguimiento"
                    >
                      <Trash2 className="mr-1.5 size-3.5 text-rose-600" aria-hidden="true" />
                      Quitar estación
                    </Button>
                  </div>

                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {productos.map((producto) => (
                      <li
                        key={producto.id}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-control border border-ink-100',
                          'bg-ink-50/60 px-2 py-1 text-[11px] text-ink-600',
                        )}
                      >
                        <span>{producto.producto || '—'}</span>
                        {producto.ultimo_precio !== null ? (
                          <span className="font-semibold text-ink-900">
                            {formatCurrency(producto.ultimo_precio)}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="text-ink-300 transition-colors hover:text-rose-600"
                          onClick={() => onQuitarProducto(producto)}
                          disabled={ocupado}
                          aria-label={`Quitar ${producto.producto} de ${producto.estacion}`}
                        >
                          <Trash2 className="size-3" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Alert
        variant="info"
        title="Cómo se vigilan los precios"
        className="xl:col-span-2"
      >
        <span className={ICONO_EN_ALERTA}>
          <RefreshCw className="size-4" aria-hidden="true" />
          Al verificar, el panel compara el precio guardado de cada producto de estas estaciones con
          el de la última consulta y anota una alerta cuando cambia más de un céntimo.
        </span>
      </Alert>
    </div>
  );
}
